import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Grid,
  Paper,
  Link
} from '@mui/material';
import {
  Email,
  Phone,
  LocationOn,
  LinkedIn,
  GitHub,
  Language,
  AccessTime
} from '@mui/icons-material';

interface ProfileHeaderProps {
  profile: {
    avatar_url?: string;
    first_name: string;
    last_name: string;
    current_title?: string;
    current_company?: string;
    actively_looking_for_job: boolean;
    job_looking_for?: string;
    email: string;
    phone?: string;
    current_location?: string;
    total_experience_years?: number;
    linkedin_url?: string;
    github_url?: string;
    portfolio_url?: string;
  };
}

export default function ProfileHeader({ profile }: ProfileHeaderProps) {
  return (
    <Paper elevation={3} sx={{ p: 4, mb: 3 }}>
      <Grid container spacing={3} alignItems="center">
        <Grid item>
          <Avatar
            src={profile.avatar_url}
            sx={{ width: 120, height: 120 }}
          >
            {profile.first_name?.[0]}{profile.last_name?.[0]}
          </Avatar>
        </Grid>
        <Grid item xs>
          <Typography variant="h4" gutterBottom>
            {profile.first_name} {profile.last_name}
          </Typography>
          {profile.current_title && (
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {profile.current_title}
              {profile.current_company && ` at ${profile.current_company}`}
            </Typography>
          )}
          {profile.actively_looking_for_job && (
            <Chip
              label="Actively looking for opportunities"
              color="success"
              size="small"
              sx={{ mt: 1 }}
            />
          )}
          {profile.job_looking_for && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Looking for: {profile.job_looking_for}
            </Typography>
          )}
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Grid container spacing={2}>
          {profile.email && (
            <Grid item>
              <Box display="flex" alignItems="center">
                <Email fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body2">{profile.email}</Typography>
              </Box>
            </Grid>
          )}
          {profile.phone && (
            <Grid item>
              <Box display="flex" alignItems="center">
                <Phone fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body2">{profile.phone}</Typography>
              </Box>
            </Grid>
          )}
          {profile.current_location && (
            <Grid item>
              <Box display="flex" alignItems="center">
                <LocationOn fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body2">{profile.current_location}</Typography>
              </Box>
            </Grid>
          )}
          {profile.total_experience_years && (
            <Grid item>
              <Box display="flex" alignItems="center">
                <AccessTime fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body2">{profile.total_experience_years} years experience</Typography>
              </Box>
            </Grid>
          )}
        </Grid>

        <Box sx={{ mt: 2 }} display="flex" gap={2}>
          {profile.linkedin_url && (
            <Link href={profile.linkedin_url} target="_blank" rel="noopener">
              <LinkedIn />
            </Link>
          )}
          {profile.github_url && (
            <Link href={profile.github_url} target="_blank" rel="noopener">
              <GitHub />
            </Link>
          )}
          {profile.portfolio_url && (
            <Link href={profile.portfolio_url} target="_blank" rel="noopener">
              <Language />
            </Link>
          )}
        </Box>
      </Box>
    </Paper>
  );
}